import React, { useContext, useEffect, useState, useRef } from 'react'
import styled, { css } from 'styled-components'
import media from 'css-in-js-media'
import Img from 'gatsby-image'

import CaretLeft from '../svg/caret-left.svg'
import CaretRight from '../svg/caret-right.svg'

import SwiperCore, { Pagination, Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

// install Swiper components
SwiperCore.use([Pagination, Navigation])

// Import Swiper styles
import 'swiper/swiper.scss'

import { Variables } from '../styles/Variables'
import { H5 } from './Typography'

import ZoomIcon from '../svg/arrows-out-simple.svg'
import CloseIcon from '../svg/close.svg'
import LayoutContext from './LayoutContext'
import { FullAbsolute } from '../styles/Utils'

export const SlideshowPagination = styled.div`
    font-family: ${Variables.font.title};
    font-size: 18px;
    line-height: 22px;
    position: absolute;
    z-index: 10;
    top: 40px;
    left: 60px;
    color: ${Variables.color.white};

    ${media('<desktop')} {
        top: 20px;
        left: 20px;
        font-size: 16px;
        line-height: 20px;
    }
`

const Arrow = styled.div`
    position: absolute;
    bottom: 40px;
    z-index: 10;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &.swiper-button-disabled {
        opacity: 0.5;
    }
    svg {        
        transition: 0.1s transform ease-out;
        will-change: transform;
    }
    svg path {        
        stroke: ${Variables.color.white};
    }
`

export const SlideshowArrowLeft = styled(Arrow)`
    left: 60px;

    ${media('<desktop')} {
        left: 20px;
        bottom: 20px;
    }

    &:active {
        svg {
            transform: translateX(-3px);
        }
    }
`

export const SlideshowArrowRight = styled(Arrow)`
    right: 60px;

    ${media('<desktop')} {
        right: 20px;
        bottom: 20px ;
    }

    &:active {
        svg {
            transform: translateX(3px);
        }
    }
`

export const SlideshowContent = styled.div`
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    right: 0;
    z-index: 10;
    text-align: center;
    opacity: 0;
    transition: 2s opacity ease-out;
    padding: 20px;
    background: rgba(0, 0, 0, 0.4);
    color: white;
    font-family: ${Variables.font.body};
    font-size: 14px;
    line-height: 120%;
    letter-spacing: 0.0025em;
    text-align: center;
`

export const SlideshowZoomButton = styled.div`
    position: absolute;
    right: 60px;
    top: 40px;
    z-index: 10;
    cursor: pointer;

    ${media('<desktop')} {
        top: 20px;
        right: 20px;
    }
    
    svg path {
        stroke: ${Variables.color.white};
    }
`

export const SlideshowSwiper = styled(Swiper)`
    padding: 0;
    height: 100%;
    width: 100%;
    background: black;

    .swiper-wrapper {
        transition-timing-function: cubic-bezier(0.29, 0.46, 0.1, 1);
    }

    ${H5} {
        font-family: ${Variables.font.body};
        font-size: 14px;
        line-height: 120%;
        letter-spacing: 0.0025em;
        text-transform: none;
    }

    .swiper-slide-active ${SlideshowContent} {
        opacity: 1;
    }
`

const CloseButton = styled.div`
    position: absolute;
    right: 80px;
    top: 42px;
    z-index: 10;
    cursor: pointer;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;

    :before {
        content: '';
        background: #fff;
        width: 42px;
        height: 42px;
        position: absolute;
        top: 50%;
        left: 50%;
        z-index: 0;
        border-radius: 50px;
        transition: 0.6s transform cubic-bezier(0.3, 0.51, 0, 1.08);

        transform: translate3d(-50%, -50%, 0) scale3d(0, 0, 0);
    }

    svg {
        position: relative;
        z-index: 1;
        vertical-align: top;

        path {
            transition: 0.3s stroke ease-out;

            stroke: ${Variables.color.lightWarmGrey};
        }
    }

    :hover {
        svg path {
            stroke: #000000;
        }
        :before {
            transform: translate3d(-50%, -50%, 0) scale3d(1, 1, 1);
        }
    }

    ${media('<desktop')} {
        top: 20px;
        right: 20px;

        svg path {
            stroke: white;
        }
        :before {
            transform: translate3d(-50%, -50%, 0) scale3d(0, 0, 0);
        }
    }
`

export const SlideshowSlideWrapper = styled.div`
    height: 0;
    padding-top: 62.5%;
    
    ${media('<desktop')} {
        height: 663px;
        padding-top: initial;
    }
`

export const SlideshowImageWrap = styled.div<{$isZoomOpen?: string}>`
    ${FullAbsolute}

    .gatsby-image-wrapper {
        max-height: 100%;
    }

    img {
        object-fit: contain !important;
        vertical-align: top;
        transform: translateZ(1px);
    }

    ${media('<desktop')} {
        ${props => !props.$isZoomOpen && css`
            .gatsby-image-wrapper {
                height: 100%;
            }
            img {
                object-fit: cover !important;
                width: 100%;
                height: 100%;
            }            
        `}
    }
`

const SlideshowSlider = ({ data, isZoom }) => {
    const { isZoomOpen, setZoomOpen, ActiveZomSlide, setActiveZomSlide } = useContext(LayoutContext)
    const [getSlideshowIndex, setSlideshowIndex] = useState(0)

    const activateZoom = (id) => {
        setZoomOpen(id)
        if (ActiveZomSlide) {
            ActiveZomSlide.map((s, i) => {
                if (s.id == id) {
                    s.slider.update()
                    s.slider.slideTo(getSlideshowIndex, 0)
                    setTimeout(() => {
                        s.slider.update()
                    }, 300)
                }
            })
        }
    }

    const deactiveZoom = () => {
        setZoomOpen(false)
    }

    return (
        <>
            {data && (
                <SlideshowSwiper
                    navigation={{
                        prevEl: '.SlideshowPrev',
                        nextEl: '.SlideshowNext',
                    }}
                    speed={1000}
                    slidesPerView="1"
                    loop={false}
                    pagination={{
                        el: '.SlideshowPagination',
                        type: 'fraction',
                    }}
                    onSlideChange={(swiper) => {
                        const newIndex = swiper.realIndex
                        setSlideshowIndex(newIndex)
                    }}
                    onSwiper={(instance) => {
                        if (isZoom && ActiveZomSlide.filter((e) => e.id === data.id).length == 0) {
                            setActiveZomSlide((ActiveZomSlide) => [
                                ...ActiveZomSlide,
                                { id: data.id, slider: instance },
                            ])
                        }
                    }}
                >
                    {data.slideshowImages &&
                        data.slideshowImages.map((slide, index) => {
                            return (
                                <SwiperSlide index={index} key={index} type={slide.type ? slide.type : undefined}>
                                    <SlideshowSlideWrapper>
                                        <SlideshowContent className="SlideshowContent">{slide.title}</SlideshowContent>
                                        <SlideshowImageWrap className="SlideshowImageWrap" $isZoomOpen={isZoomOpen}>
                                            <Img
                                                fluid={slide.fluid}
                                                durationFadeIn={200}
                                                placeholderStyle={{
                                                    display: 'none',
                                                }}
                                                alt=""
                                            />
                                        </SlideshowImageWrap>
                                    </SlideshowSlideWrapper>
                                </SwiperSlide>
                            )
                        })}
                    <SlideshowPagination className="SlideshowPagination swiper-pagination" />
                    <SlideshowArrowLeft className="SlideshowPrev">
                        <CaretLeft />
                    </SlideshowArrowLeft>
                    <SlideshowArrowRight className="SlideshowNext">
                        <CaretRight />
                    </SlideshowArrowRight>
                    {!isZoom && (
                        <SlideshowZoomButton
                            onClick={() => {
                                activateZoom(data.id)
                            }}
                        >
                            <ZoomIcon />
                        </SlideshowZoomButton>
                    )}
                    {isZoom && (
                        <CloseButton
                            onClick={() => {
                                deactiveZoom()
                            }}
                        >
                            <CloseIcon />
                        </CloseButton>
                    )}
                </SlideshowSwiper>
            )}
        </>
    )
}

export default SlideshowSlider
